import { Toast } from '@gafe/common-ui';
import { isAxiosError } from 'axios';
import { actionChannel, call, flush, put, race, select, take, takeEvery } from 'redux-saga/effects';

import { UploadsQueueActionTypes } from '@/store/reducers/uploads-queue/action-types';
import {
  changeUploadStatusToCanceled,
  changeUploadStatusToFailed,
  changeUploadStatusToInProgress,
  changeUploadStatusToSucceed,
  startUpload,
  startUploading,
} from '@/store/reducers/uploads-queue/actions';
import {
  selectFirstPendingUploadInQueue,
  selectNewUploadsInQueue,
} from '@/store/reducers/uploads-queue/selectors';
import { ReduxAction } from '@/types';

import { BucketUndefined, Upload, UploadDTO } from './types';
import { UploadsController } from './UploadsController';

export function* handleUpload() {
  const file: Upload | null = yield select(selectFirstPendingUploadInQueue);

  if (file?.bucketId) {
    try {
      yield put(changeUploadStatusToInProgress(file.id));

      const cancel = new AbortController();

      const [response, stop_all] = yield race([
        call(UploadsController.uploadFile, file, cancel),
        take(UploadsQueueActionTypes.CANCEL_ALL_UPLOADS),
      ]);

      if (stop_all) {
        cancel.abort();

        const queue = yield select(selectNewUploadsInQueue);

        for (let i = 0; i <= queue.length; i++) {
          yield put(changeUploadStatusToCanceled(queue[i].id));
        }
      }

      if (response.data) {
        yield put(changeUploadStatusToSucceed(file.id));
        yield put({
          type: UploadsQueueActionTypes.FETCH_ATTACHMENTS_LIST.SUCCESS,
          payload: response.data,
        });

        const message = 'The upload was successful';

        Toast({
          closeButton: true,
          dismissive: true,
          message,
          showIcon: true,
          title: 'Success',
          type: 'success',
        });
      }
    } catch (err) {
      if (isAxiosError(err)) {
        const errorMessage =
          err.response?.data?.validationErrors?.[0]?.error ?? err.response?.data?.message;

        yield put(changeUploadStatusToFailed(file.id, errorMessage));

        return;
      }

      yield put(changeUploadStatusToFailed(file.id));
    } finally {
      // Continue uploading
      yield put(startUpload());
    }
  }
}

function* handleUploads() {
  const channel = yield actionChannel(UploadsQueueActionTypes.START_UPLOADING);

  while (yield take(channel)) {
    // Retrieve first upload from the Redux that is waiting to be uploaded
    const upload: UploadDTO | null = yield select(selectFirstPendingUploadInQueue);

    // Upload if it exists
    if (upload && upload.bucketId !== BucketUndefined) {
      try {
        // Mark upload as "Currently uploading"
        yield put(changeUploadStatusToInProgress(upload.id));

        const abortController = new AbortController();
        const [response, stop_all, stop_current] = yield race([
          call(UploadsController.uploadFileLegacy, upload, abortController),
          take(UploadsQueueActionTypes.CANCEL_ALL_UPLOADS),
          take(
            (action: ReduxAction): boolean =>
              action.type === UploadsQueueActionTypes.CANCEL_UPLOAD && action.payload === upload.id,
          ),
        ]);

        if (stop_current) {
          yield handleCancelUpload(stop_current);
          abortController.abort();
        }

        if (stop_all) {
          abortController.abort();
          yield flush(channel);

          const queue = yield select(selectNewUploadsInQueue);

          for (let i = 0; i <= queue.length; i++) {
            yield put(changeUploadStatusToCanceled(queue[i].id));
          }
        }

        if (response) {
          // Mark upload as "Successfully uploaded"
          yield put(changeUploadStatusToSucceed(upload.id));
          yield put({
            type: UploadsQueueActionTypes.FETCH_ATTACHMENTS_LIST.SUCCESS,
            payload: [response.data],
          });
        }
      } catch {
        // Mark upload as "Uploading failed"
        yield put(changeUploadStatusToFailed(upload.id));
      } finally {
        // Continue uploading
        yield put(startUploading());
      }
    }
  }
}

function* handleFetchAttachments(action: ReduxAction) {
  try {
    const { bucketId } = action.payload;
    const { data } = yield UploadsController.getFiles(bucketId);

    yield put({
      type: UploadsQueueActionTypes.FETCH_ATTACHMENTS_LIST.SUCCESS,
      payload: data,
    });
  } catch (error) {
    yield put({ type: UploadsQueueActionTypes.FETCH_ATTACHMENTS_LIST.FAIL, error });
    console.error(error);
  }
}

function* handleDeleteAttachment(action: ReduxAction) {
  try {
    const { activityId, bucketId, uploadDTO } = action.payload;

    if (activityId) {
      yield UploadsController.deleteFile({
        activityId,
        bucketId: bucketId,
        fileId: uploadDTO.id,
      });
    } else {
      yield UploadsController.deleteFileLegacy({
        bucketId: bucketId,
        fileId: uploadDTO.id,
      });
    }
    // Notify application that file was deleted.
    // At this point this is enough for a user and is safe to do so.
    yield put({
      type: UploadsQueueActionTypes.DELETE_ATTACHMENT.SUCCESS,
      payload: { id: uploadDTO.id },
    });
  } catch (error) {
    yield put({ type: UploadsQueueActionTypes.DELETE_ATTACHMENT.FAIL, error });
    console.error(error);
  }
}

function* handleCancelUpload(action: ReduxAction) {
  yield put(changeUploadStatusToCanceled(action.payload));
}

export function* FileControllerSaga() {
  yield takeEvery(UploadsQueueActionTypes.INITIALIZE_UPLOADING_QUEUE, handleUploads);
  yield takeEvery(UploadsQueueActionTypes.DELETE_ATTACHMENT.START, handleDeleteAttachment);
  yield takeEvery(UploadsQueueActionTypes.FETCH_ATTACHMENTS_LIST.START, handleFetchAttachments);
  yield takeEvery(UploadsQueueActionTypes.CANCEL_UPLOAD, handleCancelUpload);
  yield takeEvery(UploadsQueueActionTypes.START_UPLOAD.START, handleUpload);
}
